import React from 'react';
import styles from './NavigationTabs.module.css';
import NavigationTooltip from '../NavigationTooltip';

const NavButton = ({ iconSrc, altText, isActive, onClick, title, isDisabled = false, tooltipsEnabled = true }) => {
  const tooltipText = isDisabled ? `${title} (Purchase from shop to unlock)` : title;
  
  return (
    <NavigationTooltip text={tooltipText} disabled={!tooltipsEnabled}>
      <button
        className={`${styles.navButton} ${isActive ? styles.active : ''} ${isDisabled ? styles.disabled : ''}`}
        onClick={isDisabled ? undefined : onClick}
        disabled={isDisabled}
      >
        <img src={iconSrc} alt={altText} />
      </button>
    </NavigationTooltip>
  );
};

const NavigationTabs = ({ activeTab, onTabChange, playerData, currentLocation, tooltipsEnabled = true }) => {
  const mainTabs = [
    // Combined Character & Inventory View, using characterIcon
    { id: 'overview', icon: '/img/icons/characterIcon.png', alt: 'Overview', title: 'Character & Inventory' },
    { id: 'quests', icon: '/img/buildingItems/questBoard.png', alt: 'Quests', title: 'Active Quests & Orders' },
    { id: 'skills', icon: '/img/icons/skillsIcon.png', alt: 'Skills', title: 'Skills' },
    { id: 'achievements', icon: '/img/icons/achievementIcon.png', alt: 'Achievements', title: 'Achievements' },
  ];

  // Building tabs - show all when at home, with ownership states
  const buildingTabs = [];
  if (currentLocation === 'home') {
    const buildingConfigs = [
      { id: 'furnace', icon: '/img/buildingItems/furnace.png', alt: 'Furnace', title: 'Furnace - Smelting & Charcoal' },
      { id: 'forge', icon: '/img/buildingItems/forge.png', alt: 'Forge', title: 'Forge - Advanced Crafting' },
      { id: 'sawmill', icon: '/img/buildingItems/sawmill.png', alt: 'Sawmill', title: 'Sawmill - Wood Processing' },
      { id: 'workbench', icon: '/img/buildingItems/workbench.png', alt: 'Workbench', title: 'Workbench - Basic Crafting' },
      { id: 'anvil', icon: '/img/buildingItems/anvil.png', alt: 'Anvil', title: 'Anvil - Repair & Upgrades' },
      { id: 'campfire', icon: '/img/icons/campfire_icon.png', alt: 'Campfire', title: 'Campfire - Cooking Food' },
      { id: 'brewingStation', icon: '/img/icons/brewing_icon.png', alt: 'Brewing Station', title: 'Brewing Station - Craft Potions' },
      { id: 'tanningStation', icon: '/img/icons/tanner_icon.png', alt: 'Tanning Station', title: 'Tanning Station - Leather Armor' },
      { id: 'runecraftingStation', icon: '/img/icons/runecraft_icon.png', alt: 'Runecrafting Station', title: 'Runecrafting Station - Magic Runes' },
      { id: 'landPlot', icon: '/img/icons/land_icon.png', alt: 'Land Plot', title: 'Land Plot - Farming & Crops' },
      { id: 'fletchingStation', icon: '/img/icons/fletching_icon.png', alt: 'Fletching Station', title: 'Fletching Station - Bows & Arrows' },
      { id: 'thievingDummy', icon: '/img/icons/stealing_icon.png', alt: 'Thieving Dummy', title: 'Thieving Dummy - Stealth Training' },
    ];

    buildingConfigs.forEach(building => {
      const isOwned = playerData?.buildings?.[building.id]?.owned || false;
      buildingTabs.push({
        id: `building-${building.id}`,
        icon: building.icon,
        alt: building.alt,
        title: building.title,
        isDisabled: !isOwned
      });
    });
  }

  return (
    <div className={styles.navigationTabsContainer}>
      {/* Main Navigation Row */}
      <div className={styles.mainTabsRow}>
        {mainTabs.map((tab) => (
          <NavButton
            key={tab.id}
            iconSrc={tab.icon}
            altText={tab.alt}
            title={tab.title}
            isActive={activeTab === tab.id}
            onClick={() => onTabChange(tab.id)}
            tooltipsEnabled={tooltipsEnabled}
          />
        ))}
      </div>

      {/* Building Tabs Row - only show when at home and not on overview tab (HomeView handles buildings directly) */}
      {currentLocation === 'home' && buildingTabs.length > 0 && activeTab !== 'overview' && (
        <div className={styles.buildingTabsRow}>
          {buildingTabs.map((tab) => (
            <NavButton
              key={tab.id}
              iconSrc={tab.icon}
              altText={tab.alt}
              title={tab.title}
              isActive={activeTab === tab.id && !tab.isDisabled}
              onClick={() => onTabChange(tab.id)}
              isDisabled={tab.isDisabled}
              tooltipsEnabled={tooltipsEnabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default NavigationTabs;